// Document check (/belge-kontrol) and the visit review screen. Findings, the
// visit summary and document notes come from the server in Turkish; the
// correction list pasted to the nurse stays Turkish too.
const belgeKontrol = {
  page: {
    title: 'Document Check',
    subtitle: 'Upload the visit documents; AI reads them, the checks flag the errors, you confirm.',
    newVisit: 'New visit',
  },
  tabs: {
    label: 'Document check sections',
    visits: 'Visits',
    participants: 'Participant documents',
  },
  access: {
    checking: 'Checking access…',
    denied: 'You do not have access.',
  },
  errors: { load: 'The data could not be loaded.' },
  common: { loading: 'Loading…' },
  kinds: {
    SCHEDULED: 'Scheduled visit (Week)',
    UNSCHEDULED: 'Unscheduled visit (UNS)',
    OTHER: 'Other',
  },
  kindsShort: {
    SCHEDULED: 'Scheduled (Week)',
    UNSCHEDULED: 'Unscheduled (UNS)',
    OTHER: 'Other',
  },
  visitStatus: {
    draft: 'Awaiting documents',
    processing: 'Reading',
    checked: 'In review',
    completed: 'Check completed',
  },
  visitStatusShort: {
    draft: 'No documents',
    processing: 'Reading',
    checked: 'In review',
    completed: 'Done',
  },
  docStatus: {
    queued: 'Queued',
    reading: 'Reading…',
    read: 'Read',
    unreadable: 'Unreadable',
    error: 'Error',
  },
  severity: {
    critical: 'Critical',
    warning: 'Warning',
    info: 'Info',
  },
  owners: {
    CMRN: 'Nurse',
    SITE: 'Pharmacist / Site',
    REVIEWER: 'You',
  },
  docTypes: {
    HOME_INFUSION_SDW: 'SDW – Home Infusion',
    SAMPLE_COLLECTION_SDW: 'SDW – Sample Collection',
    UNSCHEDULED_SDW: 'SDW – Unscheduled Visit',
    HOME_ASSESSMENT_SDW: 'SDW – Home Assessment',
    CHAIN_OF_CUSTODY: 'Chain of Custody',
    QUICKSTAT: 'QuickSTAT waybill',
    LRF: 'LRF (Labcorp)',
    TCCF: 'TCCF',
    CAW: 'Clinician worksheet (CAW)',
    DCF: 'DCF',
    POF: 'POF',
    POF_UNS: 'POF-UNS',
    IRR_ORDER: 'IRR order form',
    PRF: 'PRF',
    PROFORMA_INVOICE: 'Proforma invoice',
    MEET_GREET: 'Meet & greet',
    SITE_CONTACT_INFO: 'Site contact form',
    TCCA_CMRN: 'TCCA (CMRN training)',
    UNKNOWN: 'Unrecognised document',
  },
  counts: {
    critical: '{{count}} critical',
    warning_one: '{{count}} warning',
    warning_other: '{{count}} warnings',
    warning: '{{count}} warnings',
    clean: 'No issues',
    open: '{{count}} pending',
  },
  columns: {
    participantId: 'Participant',
    actions: 'Action',
    label: 'Visit',
    visitDate: 'Date',
    status: 'Status',
    findings: 'Findings',
    nurse: 'Nurse',
    createdBy: 'Created by',
    kind: 'Visit type',
    hasSamples: 'Samples',
    createdAt: 'Created',
    costUsd: 'API cost',
  },
  table: {
    listLabel: 'Visits',
    open: 'Review',
    openHint: 'Open the visit’s review screen',
    empty: 'No visits match the filters',
    noVisits: 'No visits yet. Start with “New visit”.',
    rangeOf: 'of {{total}} visits',
    ofTotal: 'out of {{total}} visits',
  },
  stats: {
    total: 'Total Visits',
    totalShort: 'Total',
    checked: 'In Review',
    checkedShort: 'In review',
    critical: 'With Critical Findings',
    criticalShort: 'Critical',
    completed: 'Check Completed',
    completedShort: 'Completed',
    share: '{{percent}}% of all visits',
  },
  status: { all: 'All' },
  toolbar: {
    search: 'Search participant, visit, nurse…',
    searchShort: 'Search visits…',
    status: 'Visit status',
    pending: 'Awaiting decision',
    pendingHint: 'Only visits with undecided findings',
    critical: 'With critical findings',
    criticalHint: 'Only visits with critical findings',
  },
  sort: {
    label: 'Sort',
    asc: 'Ascending',
    desc: 'Descending',
    createdAt: 'Created',
    visitDate: 'Visit date',
    participantId: 'Participant',
  },
  panel: {
    label: 'Selected visit',
    heading: 'Visit',
    open: 'Open review',
    delete: 'Delete visit',
    findings: 'Findings',
    noChecks: 'The checks have not run yet.',
    note: 'Note',
    counts: {
      critical: 'Critical',
      warning: 'Warnings',
      open: 'Pending',
    },
    samplesYes: 'Samples taken',
    samplesNo: 'No samples',
    completedBy: 'Check completed by',
  },
  confirm: {
    cancel: 'Cancel',
    deleteVisit: {
      title: 'Delete visit?',
      message: 'Delete this visit with all its documents and findings?',
      confirm: 'Delete visit',
    },
    deleteDocument: {
      title: 'Delete document?',
      message: 'Delete "{{name}}"?',
      confirm: 'Delete document',
    },
  },
  toast: {
    visitCreated: 'Visit created. Now upload the documents.',
    visitDeleted: 'Visit deleted.',
    demoCreated: 'Test visit created.',
    documentDeleted: 'Document deleted.',
    openFailed: 'The file could not be opened.',
    tooLarge: '{{name}} is too large (45 MB at most).',
    unsupported: 'Unsupported file: {{names}}',
    lowResolution: 'Some photos have a low resolution and may be unreadable. If you can, retake them closer or at a higher resolution.',
    copied: 'Correction list copied to the clipboard.',
    copyFailed: 'Could not copy.',
    rerunDone: 'Checks run again.',
    completed: 'Check completed.',
  },
  newVisit: {
    title: 'New visit check',
    subtitle: 'Create the visit, then upload its documents.',
    participant: 'Participant no.',
    participantError: 'The participant no. must be 4 digits - 4 digits (e.g. 1556-0002).',
    kind: 'Visit type',
    week: 'Week',
    unsNumber: 'Unscheduled visit no.',
    date: 'Visit date',
    samples: 'Samples were taken at this visit',
    samplesHint: 'Sample Collection SDW, LRF and TCCF are expected.',
    note: 'Note (optional)',
    missing: {
      participant: 'participant no. (as 1556-0002)',
      week: 'week',
      date: 'visit date',
    },
    missingPrefix: 'Still needed: {{list}}.',
    cancel: 'Cancel',
    create: 'Create',
    creating: 'Creating…',
  },
  demo: {
    button: 'Test simulation',
    title: 'Test simulation (temporary)',
    intro: 'Uses sample readings prepared with mistakes found in real documents. AI is not called and there is no cost; the checks are the same as in the real system. On the visit it creates you can confirm and dismiss findings, then delete it.',
  },
  upload: {
    title: 'Drag files here or click to choose',
    hint: 'Each PDF is read as a separate document. Photos of the same document selected together are merged into one PDF.',
    formats: 'PDF, JPG, PNG, WEBP · 45 MB at most',
  },
  documents: {
    title: 'Documents ({{count}})',
    add: 'Add document',
    closeUpload: 'Close',
    detecting: 'Detecting document type…',
    pages_one: '{{count}} page',
    pages_other: '{{count}} pages',
    pages: '{{count}} pages',
    simulation: 'Simulation',
    unreadable: 'This file could not be read, please upload it at a better resolution.',
    unreadablePages: '(Unreadable pages: {{pages}})',
    poorPages: 'Page {{pages}} could not be read clearly. Check the results by hand or upload at a better resolution.',
    poorPagesShort: 'Page {{pages}} unclear',
    failed: 'Could not process: {{error}}',
    unknownError: 'unknown error',
    open: 'Open',
    openTitle: 'Open document',
    reread: 'Read again',
    delete: 'Delete',
    none: 'No documents uploaded yet.',
    filterHint: '{{name}} – click to show its findings',
    openFindings: 'Findings to review',
    missing: 'Missing – not uploaded',
    missingHint: 'Required for this visit type',
  },
  participants: {
    listLabel: 'Participants',
    search: 'Search or add a participant no.',
    open: 'Open / add',
    openHint: 'Opens the full participant number, or adds it',
    count_one: '{{count}} participant',
    count_other: '{{count}} participants',
    count: '{{count}} participants',
    empty: 'No participants yet.',
    hasReference: 'Reference on file',
    noReference: 'No reference',
    selectHint: 'Select a participant to see their reference documents.',
    back: 'Participants',
    reference: 'Reference data',
    referenceEmpty: 'No reference data. Upload the PRF, POF and IRR order forms.',
    dob: 'Date of birth',
    bpArm: 'Blood pressure arm',
    pofValidity: 'POF validity',
    weeks: 'weeks {{from}}–{{to}}',
    route: 'Route',
    dose: 'Dose',
    irr: 'IRR order',
    irrValue: 'signed {{date}}, weeks {{from}}–{{to}}',
    documents: 'Reference documents',
    uploadHint: 'PRF (Participant Registration Form), POF and IRR Order Form. Upload new POF/IRR forms as they arrive; the latest one is used.',
    tiles: {
      total: 'Participants',
      totalShort: 'Participants',
      with: 'With Reference',
      withShort: 'With ref.',
      without: 'Without Reference',
      withoutShort: 'No ref.',
    },
  },
  visit: {
    week: 'Week {{week}}',
    test: 'TEST',
    samples: 'With samples',
    nurse: 'Nurse: {{name}}',
    cost: 'API cost ≈ {{cost}}',
    back: 'Visits',
    more: 'More actions',
    rerun: 'Run the checks again',
    delete: 'Delete visit',
    notFound: 'Visit not found.',
    uploadTitle: 'Upload the visit documents',
    uploadText: 'You can upload all documents of the visit (SDW, Chain of Custody, QuickSTAT, LRF…) at once. Document types are recognised automatically; when reading ends the checks run by themselves and the findings are listed here.',
    readingTitle: 'Reading the documents…',
    readingText: 'Once all documents are read the checks will run and the findings will be listed here.',
    demoNotice: 'Test simulation: these are not real scans but sample readings prepared with mistakes from real documents. The checks are the same as in the real system.',
    note: 'Note: {{note}}',
    ruleErrors: 'Some checks could not run: {{errors}}',
    panes: 'Visit sections',
    paneSummary: 'Nurse',
    paneDocuments: 'Documents',
  },
  verdict: {
    noDocuments: { title: 'Awaiting documents', text: 'Upload the visit documents; when reading ends the checks run by themselves.' },
    reading: { title: 'Reading the documents…', text: '{{read}} / {{total}} documents read. The checks run by themselves when it ends.' },
    completed: {
      title: 'Check completed',
      text_one: '{{name}} · {{date}} · {{count}} correction requested',
      text_other: '{{name}} · {{date}} · {{count}} corrections requested',
      text: '{{name}} · {{date}} · {{count}} corrections requested',
    },
    clean: { title: 'No issues found', text: 'The checks found nothing to correct at this visit. You can complete the check.' },
    reviewedFixes: {
      title: 'Review finished',
      text_one: '{{count}} correction needed. Copy the list and send it to the people concerned, then complete the check.',
      text_other: '{{count}} corrections needed. Copy the list and send it to the people concerned, then complete the check.',
      text: '{{count}} corrections needed. Copy the list and send it to the people concerned, then complete the check.',
    },
    reviewedClean: { title: 'Review finished', text: 'All findings were marked as "no issue".' },
    needsFix: { title: 'Corrections needed', text: '{{found}} found. Review each finding and decide.' },
    needsCheck: { title: 'There are points to check', text: '{{found}} found.' },
  },
  review: {
    ownerHint: 'Show this person’s findings',
    progress: 'Findings reviewed',
    complete: 'Complete the check',
    completeHint: 'All findings must be reviewed first',
    copy: 'Copy correction list',
    copyHint: 'No finding is marked "Needs correction"',
  },
  findings: {
    title: 'Findings',
    viewLabel: 'Findings view',
    views: {
      todo: 'To review',
      done: 'Decided',
      all: 'All',
    },
    filterOwner: 'Who: {{name}}',
    filterDoc: 'Document: {{name}}',
    clearFilter: 'Remove filter',
    cleanTitle: 'No issues found',
    cleanText: 'The checks found nothing to correct at this visit.',
    doneTitle: 'All findings reviewed',
    doneText: 'You can close the visit with “Complete the check” above.',
    closedText: 'The check of this visit is complete.',
    emptyFilter: 'No findings to review under this filter.',
    emptyView: 'No findings in this view.',
    visitWide: 'Whole visit',
    infoNotes: 'Info notes ({{count}}) – no decision needed',
  },
  finding: {
    observed: 'On the form',
    message: 'Finding',
    action: 'To do',
    page: 'Page {{page}}',
    openPage: 'Open page',
    openDocument: 'Open document',
    fix: '✓ Needs correction',
    ok: '✓ No issue',
    details: 'Details',
    hide: 'Hide',
    undo: 'Undo',
    decideFix: 'Needs correction',
    decideOk: 'No issue',
    addNote: 'Add note',
    notePlaceholder: 'Note (optional) – saved with your decision',
  },
  summary: {
    title: 'What the nurse did',
    reading: 'Reading the documents; the visit summary will appear here when it ends.',
    empty: 'Use “Run the checks again” or upload the documents to get a summary.',
    age: 'Participant is {{age}} years old',
    vitals: 'Vital signs',
    flaggedHint: 'There is a finding about this step',
    measure: 'Measurement',
    outOfRange: 'Out of range',
  },
};

export default belgeKontrol;
